import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  IFamilyConfig,
  ILightingClassConfig,
  ILuminaireLayoutConfig,
  IOptimizationConfig,
  IRoadLayoutConfig,
  IRoadwayConfig,
  q0Enum,
  RoadCoveringEnum,
} from "types";
import { StreeCalcDefaultConfig, STEPS, STEP_IDS } from "consts";
import { getTypedVal, setURL } from "utils/urls";
import { familyConfigValid, optimizationConfigValid, resultSelectionConfigValid, roadwayConfigValid } from "utils/validators";

/**
 * Hook to manage all wizard/stepper configuration state.
 * Consolidates: family, roadLayout, roadway, lightingClass, luminaireLayout,
 * optimization configs + result selection + current step + validation.
 */
export function useWizardConfig() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage;

  // Family config (initialized from URL)
  const [familyConfig, setFamilyConfig] = useState<IFamilyConfig>(() => ({ familyIds: getTypedVal("family_ids")?.split(",") ?? [] }));

  // Road layout config
  const [roadLayoutConfig, setRoadLayoutConfig] = useState<IRoadLayoutConfig>(StreeCalcDefaultConfig.roadLayoutConfig);

  // Roadway config
  const [roadwayConfig, setRoadwayConfig] = useState<IRoadwayConfig>(StreeCalcDefaultConfig.roadwayConfig);

  // Lighting class config
  const [lightingClassConfig, setLightingClassConfig] = useState<ILightingClassConfig>(StreeCalcDefaultConfig.lightingClassConfig);

  // Luminaire layout config
  const [luminaireLayoutConfig, setLuminaireLayoutConfig] = useState<ILuminaireLayoutConfig>(StreeCalcDefaultConfig.luminaireLayoutConfig);

  // Optimization config (deep clone to avoid mutation)
  const [optimizationConfig, setOptimizationConfig] = useState<IOptimizationConfig>(() => structuredClone(StreeCalcDefaultConfig.optimizationConfig));

  // Result selection
  const [selectedResult, setSelectedResult] = useState<string>(() => getTypedVal("selected_result", ""));
  const [resultFamilyIndex, setResultFamilyIndex] = useState(-1);

  // Current step (initialized from URL)
  const [currentStep, setCurrentStep] = useState<number>(() => (getTypedVal("prepare_pdf") ? 8 : 1));

  // Initialize language-dependent config values on mount (covering and q0)
  useEffect(() => {
    setRoadwayConfig((prev) => ({
      ...prev,
      covering: language === "fr" ? RoadCoveringEnum.R2 : RoadCoveringEnum.R3,
      q0: language === "fr" ? q0Enum.q0007 : q0Enum.q0008,
    }));
    setOptimizationConfig((prev) => ({
      ...prev,
      maintenanceFactor: language === "fr" ? 0.9 : StreeCalcDefaultConfig.optimizationConfig.maintenanceFactor,
    }));
  }, [i18n.resolvedLanguage]);

  // Handle result selection with URL sync — always persists selected_result regardless of htr_url_statefulurl
  const handleSelectResult = useCallback((selected: string) => {
    setSelectedResult(selected);
    setURL("selected_result", selected);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("selected_result", selected);
    window.history.replaceState(null, "", `${window.location.origin}?${urlParams.toString()}`);
  }, []);

  // Validation callback for wizard navigation.
  // targetIndex: 0-based index of the step we want to navigate TO.
  // Validates all steps before targetIndex.
  const configIsValid = useCallback(
    (targetIndex: number): boolean => {
      for (let i = 0; i < targetIndex; i++) {
        const key = STEP_IDS[i];
        if (key === STEPS.FAMILY.en && !familyConfigValid(familyConfig)) return false;
        if (key === STEPS.ROADWAY.en && !roadwayConfigValid(roadwayConfig)) return false;
        if (key === STEPS.OPTIMIZATION.en && !optimizationConfigValid(optimizationConfig)) return false;
        if (key === STEPS.RESULT_SELECTION.en && !resultSelectionConfigValid(currentStep, selectedResult)) return false;
      }
      return true;
    },
    [familyConfig, roadwayConfig, optimizationConfig, currentStep, selectedResult]
  );

  return {
    // Configs
    familyConfig,
    setFamilyConfig,
    roadLayoutConfig,
    setRoadLayoutConfig,
    roadwayConfig,
    setRoadwayConfig,
    lightingClassConfig,
    setLightingClassConfig,
    luminaireLayoutConfig,
    setLuminaireLayoutConfig,
    optimizationConfig,
    setOptimizationConfig,

    // Result selection
    selectedResult,
    setSelectedResult,
    resultFamilyIndex,
    setResultFamilyIndex,
    handleSelectResult,

    // Navigation
    currentStep,
    setCurrentStep,
    configIsValid,
  };
}
